import React from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import type { TicketSummary } from '../../../services/supportTicketsService';
import { formatVnDateTime } from '../../../utils/vnDateTime';

interface TicketsTableProps {
  data: TicketSummary[];
  loading: boolean;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize?: number) => void;
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '–';
  const formatted = formatVnDateTime(dateStr);
  return formatted === '—' ? '–' : formatted;
}

const STATUS_MAP: Record<string, { color: string; label: string }> = {
  OPEN: { color: 'blue', label: 'Mới' },
  IN_PROGRESS: { color: 'orange', label: 'Đang xử lý' },
  WAITING_CUSTOMER: { color: 'gold', label: 'Cần phản hồi' },
  RESOLVED: { color: 'green', label: 'Đã giải quyết' },
  CLOSED: { color: 'default', label: 'Đã đóng' },
};

const PRIORITY_MAP: Record<string, { color: string; label: string }> = {
  LOW: { color: 'default', label: 'Thấp' },
  MEDIUM: { color: 'blue', label: 'TB' },
  HIGH: { color: 'orange', label: 'Cao' },
  URGENT: { color: 'red', label: 'Khẩn cấp' },
};

export const TicketsTable: React.FC<TicketsTableProps> = ({
  data,
  loading,
  page,
  pageSize,
  total,
  onPageChange,
}) => {
  const navigate = useNavigate();

  const columns: ColumnsType<TicketSummary> = [
    {
      title: 'Mã ticket',
      dataIndex: 'ticketCode',
      key: 'ticketCode',
      width: 140,
      render: (code: string, record) => (
        <button
          type="button"
          onClick={() => navigate(`/support-tickets/${record.id}`)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            color: '#1890ff',
            textDecoration: 'underline',
          }}
        >
          {code}
        </button>
      ),
    },
    {
      title: 'Nguồn',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source: string | undefined) => {
        const isUser = source === 'USER';
        return (
          <Tag color={isUser ? 'green' : 'blue'}>
            {isUser ? 'User' : 'Merchant'}
          </Tag>
        );
      },
    },
    {
      title: 'Người tạo',
      key: 'merchant',
      width: 180,
      render: (_: unknown, record: TicketSummary) => (
        <span>
          {record.merchant?.storeName || record.merchant?.name || '–'}
          {record.merchant?.phone && (
            <span style={{ color: '#888', fontSize: 12, display: 'block' }}>
              {record.merchant.phone}
            </span>
          )}
        </span>
      ),
    },
    {
      title: 'Danh mục',
      dataIndex: 'category',
      key: 'category',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const s = STATUS_MAP[status] || { color: 'default', label: status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Ưu tiên',
      dataIndex: 'priority',
      key: 'priority',
      width: 90,
      render: (priority: string) => {
        const p = PRIORITY_MAP[priority] || {
          color: 'default',
          label: priority,
        };
        return <Tag color={p.color}>{p.label}</Tag>;
      },
    },
    {
      title: 'Tin nhắn cuối',
      key: 'lastReply',
      width: 160,
      ellipsis: true,
      render: (_: unknown, record: TicketSummary) => (
        <span title={record.lastMessagePreview || ''}>
          {record.lastMessagePreview || '–'}
        </span>
      ),
    },
    {
      title: 'Cập nhật',
      dataIndex: 'lastMessageAt',
      key: 'lastMessageAt',
      width: 120,
      render: (v: string | null | undefined) => formatDate(v),
    },
    {
      title: 'Người xử lý',
      key: 'assignee',
      width: 120,
      render: (_: unknown, record: TicketSummary) =>
        record.assignedAdmin?.name || '–',
    },
  ];

  return (
    <Table<TicketSummary>
      rowKey="id"
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={{
        current: page,
        pageSize,
        total,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (t, range) => `${range[0]}-${range[1]} / ${t}`,
      }}
      onChange={pagination => {
        const current = pagination?.current ?? page;
        const size = pagination?.pageSize ?? pageSize;
        onPageChange(current, size);
      }}
      rowClassName={record =>
        (record.adminUnreadCount ?? 0) > 0 ? 'support-ticket-row-unread' : ''
      }
      onRow={record => ({
        onClick: () => navigate(`/support-tickets/${record.id}`),
        style: { cursor: 'pointer' },
      })}
    />
  );
};
